import {
  loadNativeOfflineBootstrap,
  primeNativeOfflineQueryCache,
} from './offline-db/bootstrap-cache';
import { readAuthSessionClaims } from '../auth-session-claims';
import { hydrateNativeSupabaseAuth, readNativeAuthSessionState } from './auth-persistence';
import { primeNativeNetworkStatus } from './network-status';
import { preloadNativeHomeRoute } from './native-route-preload';
import { isNativePlatform } from './platform';

import type { QueryClient } from '@tanstack/react-query';
import type { Session, SupabaseClient } from '@supabase/supabase-js';

/** Prepare a safe first frame without making auth-js's refresh request load-bearing. */
export async function prepareNativeAuthBootstrap(
  supabase: SupabaseClient,
  queryClient: QueryClient,
  shouldCommit: () => boolean = () => true,
): Promise<Session | null> {
  const storedSessionPromise = hydrateNativeSupabaseAuth(supabase);
  const routePreloadPromise = isNativePlatform() ? preloadNativeHomeRoute() : Promise.resolve();
  // Connectivity improves the first presentation but is not an authority on
  // identity and a wedged plugin bridge must never hold the boot screen open.
  void primeNativeNetworkStatus();
  const storedSession = await storedSessionPromise;
  if (!storedSession) {
    await routePreloadPromise;
    return null;
  }

  const [bootstrap] = await Promise.all([
    loadNativeOfflineBootstrap(storedSession),
    routePreloadPromise,
  ]);
  if (!bootstrap) {
    return null;
  }

  // Startup has a deadline. Reads and route imports may finish after it, but
  // stale work must never seed authenticated data into a released app tree.
  if (!shouldCommit()) {
    return null;
  }

  primeNativeOfflineQueryCache(queryClient, storedSession, bootstrap);
  return storedSession;
}

export function isSameAuthSession(left: Session, right: Session): boolean {
  if (left.user.id !== right.user.id) {
    return false;
  }
  const leftClaims = readAuthSessionClaims(left.access_token);
  const rightClaims = readAuthSessionClaims(right.access_token);
  if (leftClaims?.sessionId && rightClaims?.sessionId) {
    return leftClaims.sessionId === rightClaims.sessionId;
  }
  return left.access_token === right.access_token;
}

export async function shouldRetainOfflineBootstrap(
  event: string,
  nextSession: Session | null,
  bootstrapSession: Session | null,
): Promise<boolean> {
  if (event !== 'INITIAL_SESSION' || nextSession || !bootstrapSession) {
    return false;
  }
  const persisted = await readNativeAuthSessionState();
  if (persisted.status === 'unavailable') {
    // Storage uncertainty is not a revocation signal. A determinate
    // SIGNED_OUT event remains authoritative and tears the bootstrap down.
    return true;
  }
  return persisted.session !== null && isSameAuthSession(bootstrapSession, persisted.session);
}

export function didAuthSessionEnd(
  event: string,
  nextSession: Session | null,
  hadSession: boolean,
  hasBootstrapSession: boolean,
): boolean {
  return (
    event === 'SIGNED_OUT' ||
    (!nextSession && (event !== 'INITIAL_SESSION' || hasBootstrapSession) && hadSession)
  );
}

function resolvePriorUserId(
  currentSession: Session | null,
  previousUserIdOverride: string | null | undefined,
): string | null {
  if (previousUserIdOverride === null) {
    return null;
  }
  return previousUserIdOverride ?? currentSession?.user.id ?? null;
}

export function didAuthIdentityChange(
  nextSession: Session | null,
  currentSession: Session | null,
  previousUserIdOverride: string | null | undefined,
  forceIdentityReplacement: boolean,
): boolean {
  const priorUserId = resolvePriorUserId(currentSession, previousUserIdOverride);
  return (
    forceIdentityReplacement ||
    Boolean(priorUserId && nextSession && priorUserId !== nextSession.user.id)
  );
}

/**
 * Whether an incoming event only re-states the session already published.
 *
 * auth-js re-notifies `SIGNED_IN` with a freshly deserialized copy of the
 * stored session on every hidden -> visible transition
 * (`GoTrueClient#_recoverAndRefresh`), so switching windows hands the app a new
 * session object carrying identical credentials. Republishing it swaps the
 * identity every consumer keys on for no change in authorization.
 *
 * Only `SIGNED_IN` qualifies, because that is the sole event auth-js delivers
 * redundantly. An allow-list rather than a deny-list of teardown events:
 * `USER_UPDATED` also arrives on unchanged credentials — auth-js re-saves
 * `{...session, user}` and notifies without touching the tokens — so suppressing
 * by credentials alone would strand a stale user in state.
 *
 * Credential equality is byte-for-byte, deliberately stricter than
 * {@link isSameAuthSession}: that one matches on the `session_id` claim and so
 * treats a refreshed — or MFA-upgraded — token as the same session, while a
 * caller suppressing work here must see every claim change, `aal` included.
 */
export function isRedundantSessionRepublish(
  event: string,
  nextSession: Session | null,
  publishedSession: Session | null,
  changedAccount: boolean,
): boolean {
  if (event !== 'SIGNED_IN' || changedAccount || !nextSession || !publishedSession) {
    return false;
  }
  return (
    nextSession.user.id === publishedSession.user.id &&
    nextSession.access_token === publishedSession.access_token &&
    nextSession.refresh_token === publishedSession.refresh_token
  );
}
